import { useEffect, useState, useCallback } from 'react'
import {
  Loader2, AlertCircle, CheckCircle2, Clock, XCircle,
  ChevronRight, X, Send, RefreshCw, ClipboardList, Layers,
  GitCommitHorizontal, ArrowRight
} from 'lucide-react'
import { workflowsApi } from '@/lib/api'
import type { WorkflowInstance, WorkflowInstanceDetail, WorkflowTask, WorkflowTransition } from '@/types'

type HistoryEntry = {
  id: number
  from_state_name?: string
  to_state_name: string
  to_state_color?: string
  action_name?: string
  performed_by_name?: string
  comment?: string
  created_at: string
}

/* ─── helpers ──────────────────────────────────────────────────────────────── */

function StateBadge({ name, color }: { name: string; color?: string }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium text-white"
      style={{ backgroundColor: color || '#6B7280' }}
    >
      {name}
    </span>
  )
}

function TaskStatusIcon({ status }: { status: WorkflowTask['status'] }) {
  if (status === 'completed') return <CheckCircle2 className="h-4 w-4 text-green-500" />
  if (status === 'cancelled') return <XCircle className="h-4 w-4 text-gray-400" />
  return <Clock className="h-4 w-4 text-amber-500 animate-pulse" />
}

function relativeDate(iso: string) {
  const d = new Date(iso)
  const diff = (Date.now() - d.getTime()) / 1000
  if (diff < 60) return 'agora'
  if (diff < 3600) return `${Math.floor(diff / 60)}m`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`
  return `${Math.floor(diff / 86400)}d`
}

/* ─── Timeline component ─────────────────────────────────────────────────── */

function InstanceTimeline({ instanceId }: { instanceId: number }) {
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    workflowsApi.getInstanceHistory(instanceId)
      .then(r => setHistory(r.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [instanceId])

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
  if (history.length === 0) return <p className="text-sm text-gray-400 py-2">Sem histórico registado ainda.</p>

  return (
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-4 top-2 bottom-2 w-px bg-gray-200" />
      <div className="space-y-4">
        {history.map((entry, i) => {
          const isLast = i === history.length - 1
          return (
            <div key={entry.id} className="relative flex gap-4 pl-10">
              {/* Dot */}
              <div
                className={`absolute left-2.5 top-1 h-3 w-3 rounded-full border-2 border-white shadow ${isLast ? 'ring-2 ring-offset-1' : ''}`}
                style={{ backgroundColor: entry.to_state_color || '#6B7280' }}
              />
              {/* Content */}
              <div className="flex-1 min-w-0 pb-1">
                <div className="flex items-center gap-2 flex-wrap">
                  {entry.from_state_name && (
                    <>
                      <span className="text-xs text-gray-500">{entry.from_state_name}</span>
                      <ArrowRight className="h-3 w-3 text-gray-400 flex-shrink-0" />
                    </>
                  )}
                  <span
                    className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold text-white"
                    style={{ backgroundColor: entry.to_state_color || '#6B7280' }}
                  >
                    {entry.to_state_name}
                  </span>
                  {entry.action_name && (
                    <span className="text-xs font-medium text-gray-700">· {entry.action_name}</span>
                  )}
                </div>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-400">
                  {entry.performed_by_name && <span>{entry.performed_by_name}</span>}
                  <span>·</span>
                  <span>{new Date(entry.created_at).toLocaleString('pt-PT', { dateStyle: 'short', timeStyle: 'short' })}</span>
                </div>
                {entry.comment && (
                  <p className="mt-1 rounded bg-gray-50 border border-gray-100 px-2 py-1 text-xs text-gray-600 italic">
                    "{entry.comment}"
                  </p>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ─── Approval panel ────────────────────────────────────────────────────────── */

function ApprovalPanel({
  instance,
  onClose,
  onApproved,
}: {
  instance: WorkflowInstanceDetail
  onClose: () => void
  onApproved: () => void
}) {
  const [comment, setComment] = useState('')
  const [selectedTransition, setSelectedTransition] = useState<WorkflowTransition | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const pendingTasks = instance.tasks.filter(t => t.status === 'pending')
  const hasPendingTasks = pendingTasks.length > 0
  const hasTransitions = instance.available_transitions.length > 0

  // Auto-select first transition if only one
  useEffect(() => {
    const transitions = instance.available_transitions
    if (transitions.length === 1) setSelectedTransition(transitions[0])
  }, [instance.available_transitions])

  const handleApprove = async () => {
    if (!selectedTransition) return
    const task = pendingTasks[0]
    if (!task) return
    setBusy(true)
    setErr(null)
    try {
      await workflowsApi.approveTask(task.id, {
        transition_id: selectedTransition.id,
        comment: comment.trim() || undefined,
      })
      onApproved()
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Erro ao aprovar tarefa')
    } finally {
      setBusy(false)
    }
  }

  const handleAdvance = async () => {
    if (!selectedTransition) return
    setBusy(true)
    setErr(null)
    try {
      await workflowsApi.advanceInstance(instance.id, {
        transition_id: selectedTransition.id,
        comment: comment.trim() || undefined,
      })
      onApproved()
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Erro ao avançar instância')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/30" onClick={onClose} />

      {/* Panel */}
      <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col h-full overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <p className="text-xs text-gray-500 mb-1">Instância #{instance.id}</p>
            <h2 className="text-lg font-semibold text-gray-900">{instance.workflow_name}</h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 hover:bg-gray-100">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* State */}
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500">Estado atual:</span>
            <StateBadge name={instance.current_state_name} color={instance.current_state_color} />
          </div>

          {/* Info */}
          <div className="rounded-lg bg-gray-50 p-4 text-sm space-y-1.5">
            <div className="flex justify-between">
              <span className="text-gray-500">Submetido por</span>
              <span className="font-medium">{instance.started_by_name}</span>
            </div>
            {instance.form_submission_id && (
              <div className="flex justify-between">
                <span className="text-gray-500">Submissão</span>
                <span className="font-medium">#{instance.form_submission_id}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">Iniciado</span>
              <span className="font-medium">{new Date(instance.started_at).toLocaleString('pt-PT')}</span>
            </div>
          </div>

          {/* Timeline */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
              <GitCommitHorizontal className="h-4 w-4" />
              Histórico de atividade
            </h3>
            <InstanceTimeline instanceId={instance.id} />
          </div>

          {/* Tasks */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Tarefas</h3>
            {instance.tasks.length === 0 ? (
              <p className="text-sm text-gray-400">Sem tarefas</p>
            ) : (
              <div className="space-y-2">
                {instance.tasks.map(t => (
                  <div key={t.id} className="flex items-center gap-2 rounded-lg border p-3">
                    <TaskStatusIcon status={t.status} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{t.task_name}</p>
                      {t.description && (
                        <p className="text-xs text-gray-500 truncate">{t.description}</p>
                      )}
                    </div>
                    {t.due_date && (
                      <span className={`text-xs ${t.is_overdue ? 'text-red-600 font-semibold' : 'text-gray-400'}`}>
                        {t.is_overdue ? '⚠ ' : ''}{new Date(t.due_date).toLocaleDateString('pt-PT')}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action section — shown whenever there are transitions available */}
          {hasTransitions && !instance.is_final_state && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-4">
              <h3 className="text-sm font-semibold text-blue-800">
                {hasPendingTasks ? 'Aprovação / Decisão' : 'Avançar fluxo'}
              </h3>

              {/* Transition selector */}
              {instance.available_transitions.length > 1 ? (
                <div className="space-y-2">
                  <p className="text-xs text-blue-700">Selecione a transição:</p>
                  {instance.available_transitions.map(t => (
                    <label key={t.id} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="transition"
                        checked={selectedTransition?.id === t.id}
                        onChange={() => setSelectedTransition(t)}
                        className="accent-blue-600"
                      />
                      <span className="text-sm font-medium text-gray-800">{t.action_name}</span>
                      <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                      <span className="text-xs text-gray-500">{t.to_state_name}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <ChevronRight className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-medium text-gray-800">{selectedTransition?.action_name}</span>
                  <span className="text-xs text-gray-500">→ {selectedTransition?.to_state_name}</span>
                </div>
              )}

              <textarea
                rows={2}
                placeholder="Comentário (opcional)"
                value={comment}
                onChange={e => setComment(e.target.value)}
                className="w-full rounded-md border border-blue-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              {err && (
                <p className="flex items-center gap-1.5 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" /> {err}
                </p>
              )}

              <button
                onClick={hasPendingTasks ? handleApprove : handleAdvance}
                disabled={busy || !selectedTransition}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {selectedTransition ? selectedTransition.action_name : 'Selecione uma transição'}
              </button>
            </div>
          )}

          {instance.is_final_state && (
            <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 p-4 text-green-700 text-sm">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              Fluxo concluído
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ─── Main page ─────────────────────────────────────────────────────────────── */

export default function WorkflowInstancesPage() {
  const [tab, setTab] = useState<'instances' | 'tasks'>('instances')
  const [instances, setInstances] = useState<WorkflowInstance[]>([])
  const [tasks, setTasks] = useState<WorkflowTask[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selected, setSelected] = useState<WorkflowInstanceDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [instRes, taskRes] = await Promise.all([
        workflowsApi.listInstances(),
        workflowsApi.myTasks(),
      ])
      setInstances(instRes.data)
      setTasks(taskRes.data)
    } catch {
      setError('Erro ao carregar dados')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const openDetail = async (id: number) => {
    setLoadingDetail(true)
    try {
      const res = await workflowsApi.getInstance(id)
      setSelected(res.data)
    } catch {
      setError('Erro ao carregar instância')
    } finally {
      setLoadingDetail(false)
    }
  }

  const handleApproved = () => {
    setSelected(null)
    load()
  }

  const pendingCount = tasks.filter(t => t.status === 'pending').length

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Acompanhamento de Fluxos</h1>
          <p className="mt-1 text-sm text-gray-600">
            Monitorize e aprove instâncias de workflow em curso
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex gap-1 rounded-xl bg-gray-100 p-1 w-fit">
        <button
          onClick={() => setTab('instances')}
          className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'instances' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Layers className="h-4 w-4" />
          Instâncias
          <span className={`ml-1 rounded-full px-2 py-0.5 text-xs font-semibold ${
            tab === 'instances' ? 'bg-primary-100 text-primary-700' : 'bg-gray-200 text-gray-600'
          }`}>{instances.length}</span>
        </button>
        <button
          onClick={() => setTab('tasks')}
          className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'tasks' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ClipboardList className="h-4 w-4" />
          Minhas Tarefas
          {pendingCount > 0 && (
            <span className="ml-1 rounded-full bg-amber-500 px-2 py-0.5 text-xs font-semibold text-white">
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-lg bg-red-50 p-4 text-red-700">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {loading && (
        <div className="mt-12 flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
        </div>
      )}

      {/* ── Instances tab ── */}
      {!loading && tab === 'instances' && (
        <div className="mt-6">
          {instances.length === 0 ? (
            <div className="mt-12 text-center">
              <Layers className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-4 text-gray-500">Nenhuma instância ativa</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Workflow</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Submissão</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Iniciado por</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Há</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {instances.map(inst => {
                    const hasTask = tasks.some(
                      t => t.instance_id === inst.id && t.status === 'pending'
                    )
                    return (
                      <tr
                        key={inst.id}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => openDetail(inst.id)}
                      >
                        <td className="px-4 py-3 text-sm font-mono text-gray-500">#{inst.id}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{inst.workflow_name}</td>
                        <td className="px-4 py-3">
                          <StateBadge name={inst.current_state_name} color={inst.current_state_color} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {inst.form_submission_id ? `#${inst.form_submission_id}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{inst.started_by_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-400">{relativeDate(inst.started_at)}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            {hasTask && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">
                                <Clock className="h-3 w-3" /> Aguarda
                              </span>
                            )}
                            {inst.is_final_state && (
                              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                                <CheckCircle2 className="h-3 w-3" /> Concluído
                              </span>
                            )}
                            {loadingDetail ? (
                              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-400" />
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Tasks tab ── */}
      {!loading && tab === 'tasks' && (
        <div className="mt-6">
          {tasks.length === 0 ? (
            <div className="mt-12 text-center">
              <ClipboardList className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-4 text-gray-500">Nenhuma tarefa pendente</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tasks.map(task => (
                <TaskApprovalCard
                  key={task.id}
                  task={task}
                  onApproved={handleApproved}
                  onViewInstance={() => task.instance_id && openDetail(task.instance_id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Side panel */}
      {selected && (
        <ApprovalPanel
          instance={selected}
          onClose={() => setSelected(null)}
          onApproved={handleApproved}
        />
      )}
    </div>
  )
}

/* ─── Task approval card (used in tasks tab) ─────────────────────────────── */

function TaskApprovalCard({
  task,
  onApproved,
  onViewInstance,
}: {
  task: WorkflowTask
  onApproved: () => void
  onViewInstance: () => void
}) {
  const [selectedTransition, setSelectedTransition] = useState<WorkflowTransition | null>(
    task.available_transitions?.length === 1 ? (task.available_transitions[0] ?? null) : null
  )
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const transitions = task.available_transitions || []

  const handleApprove = async () => {
    if (!selectedTransition) return
    setBusy(true)
    setErr(null)
    try {
      await workflowsApi.approveTask(task.id, {
        transition_id: selectedTransition.id,
        comment: comment.trim() || undefined,
      })
      setDone(true)
      setTimeout(onApproved, 800)
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Erro ao aprovar')
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 p-4 text-green-700">
        <CheckCircle2 className="h-5 w-5" />
        <span className="text-sm font-medium">Transição aplicada com sucesso!</span>
      </div>
    )
  }

  return (
    <div className={`rounded-xl border bg-white p-5 shadow-sm ${task.is_overdue ? 'border-red-300' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <TaskStatusIcon status={task.status} />
            <span className="text-sm font-semibold text-gray-900">{task.task_name}</span>
            {task.is_overdue && (
              <span className="text-xs font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Em atraso</span>
            )}
          </div>
          {task.description && (
            <p className="text-sm text-gray-500 mb-2">{task.description}</p>
          )}
          <div className="flex items-center gap-3 text-xs text-gray-400">
            {task.workflow_name && <span>{task.workflow_name}</span>}
            {task.current_state_name && (
              <>
                <span>·</span>
                <StateBadge name={task.current_state_name} color={task.current_state_color} />
              </>
            )}
            {task.due_date && (
              <>
                <span>·</span>
                <span>Prazo: {new Date(task.due_date).toLocaleDateString('pt-PT')}</span>
              </>
            )}
          </div>
        </div>
        <button
          onClick={onViewInstance}
          className="flex-shrink-0 text-xs text-blue-600 hover:underline"
        >
          Ver instância
        </button>
      </div>

      {transitions.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
          {/* Transition picker */}
          {transitions.length > 1 ? (
            <div className="flex flex-wrap gap-2">
              {transitions.map(t => (
                <button
                  key={t.id}
                  onClick={() => setSelectedTransition(t)}
                  className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                    selectedTransition?.id === t.id
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {t.action_name}
                  <span className="ml-1.5 text-xs text-gray-400">→ {t.to_state_name}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <span className="font-medium">{transitions[0].action_name}</span>
              <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
              <span className="text-gray-500">{transitions[0].to_state_name}</span>
            </div>
          )}

          <textarea
            rows={1}
            placeholder="Comentário opcional..."
            value={comment}
            onChange={e => setComment(e.target.value)}
            className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {err && <p className="text-sm text-red-600">{err}</p>}

          <button
            onClick={handleApprove}
            disabled={busy || !selectedTransition}
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {selectedTransition ? selectedTransition.action_name : 'Selecione uma ação'}
          </button>
        </div>
      )}
    </div>
  )
}
